import type { InteractionMode } from "./interactionModes";
import type { MCQQuestion, RolePlayScenario } from "@/lib/powtoon-be/types";
import { createLogger } from "@/lib/frontend-logger";
import { CORRELATION_HEADER, nextCorrelationId } from "@/lib/correlation";
import { appendOp } from "@/lib/opSlug";
import { normalizeScenario } from "./modes/role_play/schema";

const logger = createLogger("scenarios.generateClient");

/**
 * Wire shape for generate-scenario-content. Uploads carry raw bytes
 * (base64) and a MIME type — the edge function decides whether to feed
 * Gemini natively (PDF/image) or extract text server-side (DOCX/PPTX/txt).
 * Nothing here is persisted.
 */
export type GenerateSourceItem =
  | {
      kind: "upload";
      file_name: string;
      mime_type: string;
      data_base64: string;
      size?: number;
    }
  | {
      kind: "file_uri";
      file_name: string;
      mime_type: string;
      file_uri: string;
      size?: number;
    }
  | { kind: "url"; url: string }
  | { kind: "text"; raw_text: string };

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

async function post<T>(fn: string, data: unknown, op?: string): Promise<T> {
  const correlationId = nextCorrelationId();
  const startedAt = performance.now();
  let res: Response;
  try {
    res = await fetch(appendOp(`${SUPABASE_URL}/functions/v1/${fn}`, op), {
      method: "POST",
      headers: {
        "content-type": "application/json",
        apikey: SUPABASE_KEY,
        authorization: `Bearer ${SUPABASE_KEY}`,
        [CORRELATION_HEADER]: correlationId,
      },
      body: JSON.stringify(data),
    });
  } catch (e) {
    logger.error("generate network error", {
      fn,
      correlationId,
      error: (e as Error).message,
    });
    throw new Error(
      `Network error reaching the AI service (correlation ${correlationId}). Check your connection and retry.`,
    );
  }
  const durationMs = Math.round(performance.now() - startedAt);
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    logger.error("generate call failed", {
      fn,
      status: res.status,
      correlationId,
      durationMs,
      error: body?.error,
      diagnostics: body?.diagnostics,
      warnings: body?.warnings,
    });
    throw new Error(formatPostError(body, res.status, correlationId));
  }
  logger.info("generate call ok", { fn, status: res.status, correlationId, durationMs });
  return body as T;
}

function formatPostError(body: unknown, status: number, correlationId: string): string {
  const b = typeof body === "object" && body !== null ? (body as Record<string, unknown>) : {};
  let base: string;
  if (typeof b.error === "string" && b.error.trim()) {
    base = b.error.trim();
  } else if (b.error && typeof b.error === "object") {
    // Zod flatten(): { fieldErrors: { field: [msg] }, formErrors: [msg] }
    const fieldErrors = (b.error as Record<string, unknown>).fieldErrors;
    if (fieldErrors && typeof fieldErrors === "object") {
      const [field, msgs] = Object.entries(fieldErrors)[0] ?? [];
      if (field && Array.isArray(msgs) && msgs.length) {
        base = `Invalid input: ${field} — ${msgs[0]}`;
      } else {
        base = "Invalid input sent to the AI service.";
      }
    } else {
      base = "Invalid input sent to the AI service.";
    }
  } else {
    base = `AI service request failed (HTTP ${status}).`;
  }

  const extras: string[] = [];
  const diag = b.diagnostics as Record<string, unknown> | undefined;
  if (diag?.blockReason) extras.push(`blocked by safety filter (${diag.blockReason})`);
  else if (diag?.finishReason && diag.finishReason !== "STOP")
    extras.push(`finish: ${diag.finishReason}`);
  if (Array.isArray(b.warnings) && b.warnings.length) {
    const w = b.warnings
      .slice(0, 3)
      .map(
        (x) =>
          `${(x as Record<string, unknown>)?.file_name ?? "source"}: ${(x as Record<string, unknown>)?.error ?? ""}`,
      )
      .join("; ");
    extras.push(`source issues — ${w}`);
  }

  return extras.length
    ? `${base} (${extras.join("; ")}) [ref ${correlationId}]`
    : `${base} [ref ${correlationId}]`;
}

export function generateScenarioContent(input: {
  mode: InteractionMode;
  inputs: GenerateSourceItem[];
  mcq?: { count: number };
}) {
  return post<{
    content: string;
    questions?: MCQQuestion[];
    warnings?: { file_name?: string; error: string }[];
  }>("generate-scenario-content", input, `generate-content-${input.mode.replace(/_/g, "-")}`);
}

export function generateScenarioField(input: {
  field:
    | "persona"
    | "opening_text"
    | "description"
    | "roleplay_avatar_personality"
    | "roleplay_hidden_information"
    | "roleplay_emotional_progression"
    | "roleplay_framing_script";
  mode: InteractionMode;
  knowledge: string;
  persona: string;
  currentValue: string;
  instructions?: string;
  name?: string;
  roleplay?: {
    goal?: string;
    avatar_role?: string;
    learner_role?: string;
    emotional_state?: string;
    avatar_personality?: string;
    framing_script?: string;
  } | null;
}) {
  return post<{ content: string }>(
    "generate-scenario-field",
    input,
    `generate-field-${input.field.replace(/_/g, "-")}`,
  );
}

// ---------- Role-play helpers (use the same shared `post`) ----------

export type RolePlayFieldKey =
  | "roleplay_avatar_personality"
  | "roleplay_hidden_information"
  | "roleplay_emotional_progression"
  | "roleplay_framing_script";

export async function generateRoleplayGoalOptions(input: {
  inputs: GenerateSourceItem[];
}): Promise<{ options: string[]; warnings: { file_name?: string; error: string }[] }> {
  const res = await post<{
    options?: string[];
    warnings?: { file_name?: string; error: string }[];
  }>(
    "generate-scenario-content",
    {
      mode: "role_play",
      inputs: input.inputs,
      roleplay: { kind: "goal_options" },
    },
    "generate-roleplay-goal-options",
  );
  const arr = Array.isArray(res.options) ? res.options : [];
  return {
    options: arr
      .filter((s): s is string => typeof s === "string" && s.trim().length > 0)
      .slice(0, 3),
    warnings: res.warnings ?? [],
  };
}

export async function generateRoleplayScenarios(input: {
  inputs: GenerateSourceItem[];
  goal: string;
  count: number;
}): Promise<{ scenarios: RolePlayScenario[]; warnings: { file_name?: string; error: string }[] }> {
  const res = await post<{
    scenarios?: unknown[];
    warnings?: { file_name?: string; error: string }[];
  }>(
    "generate-scenario-content",
    {
      mode: "role_play",
      inputs: input.inputs,
      roleplay: { kind: "scenarios", goal: input.goal, count: input.count },
    },
    "generate-roleplay-scenarios",
  );
  const arr = Array.isArray(res.scenarios) ? res.scenarios : [];
  return { scenarios: arr.map(normalizeScenario), warnings: res.warnings ?? [] };
}

export async function enhanceRoleplayField(input: {
  field: RolePlayFieldKey;
  currentValue: string;
  scenarioContext: string;
  knowledge: string;
}): Promise<string> {
  const res = await generateScenarioField({
    field: input.field,
    mode: "role_play",
    knowledge: input.knowledge,
    persona: input.scenarioContext,
    currentValue: input.currentValue,
  });
  return res.content;
}
